import {
  TacoAssetType,
  AssetUploadUrlRequest,
  AssetUploadUrlResponse,
  AdditionalAssetMeta,
  AssetManifestEntry,
  ListUploadedFilesResponse,
} from "@amzn/afxtaco-types";
import { apiGet, apiPost } from "../apiUtils";

/**
 * Response structure for asset download URL
 */
export interface AssetDownloadUrlResponse {
  success: boolean;
  data: {
    presignedUrl: string;
  };
  requestId?: string;
}

/**
 * Query parameters for requesting an asset download URL.
 * `versionNumber` is optional. When omitted, the API resolves to the
 * project's latest version (requires the `projectDownload` feature flag
 * to be enabled for the caller server-side).
 */
export interface AssetDownloadUrlParams {
  projectId: string;
  /** Omit to read the current file; set to fetch a sealed historical version. */
  versionNumber?: string | number;
  assetType: TacoAssetType;
}

/**
 * Retrieves a presigned URL for downloading a specific asset
 *
 * @param params - Query parameters including projectId, versionNumber, and assetType
 * @returns Promise resolving to presigned URL response
 * @throws Error if the API request fails or returns invalid data
 *
 * @example
 * ```typescript
 * const urlResponse = await getAssetDownloadUrl({
 *   projectId: "project-123",
 *   versionNumber: "1",
 *   assetType: "AUDIO"
 * });
 * console.log(urlResponse.data.presignedUrl);
 * ```
 */
export const getAssetDownloadUrl = async (
  params: AssetDownloadUrlParams,
): Promise<AssetDownloadUrlResponse> => {
  // Omit versionNumber when undefined — that asks the server for the current
  // file (a value selects a sealed historical version, future picker).
  // Preserves the historical query string ordering (projectId, versionNumber,
  // assetType) so existing API contracts and recorded fixtures remain stable.
  const queryParams: Record<string, string> = { projectId: params.projectId };
  if (params.versionNumber !== undefined) {
    queryParams["versionNumber"] = params.versionNumber.toString();
  }
  queryParams["assetType"] = params.assetType;

  const restOperation = apiGet("/assets/downloadUrl", { queryParams });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as AssetDownloadUrlResponse;
};

/**
 * API response for listing a project's additional (non-canonical) assets.
 */
export interface ListAdditionalAssetsResponse {
  success: boolean;
  data: {
    assets: AdditionalAssetMeta[];
  };
  requestId?: string;
}

/**
 * Lists the "additional" assets for a project — the arbitrary, non-canonical
 * files the agent generated (e.g. `research/notes.md`, a `.csv`) that don't have
 * a dedicated tab. Gated client-side behind the `additionalAssets` feature flag.
 *
 * @param projectId - the project whose additional assets to list
 * @returns Promise resolving to the list response
 * @throws Error if the API request fails
 */
export const listAdditionalAssets = async (
  projectId: string,
): Promise<ListAdditionalAssetsResponse> => {
  const restOperation = apiGet("/assets/list", {
    queryParams: { projectId },
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as ListAdditionalAssetsResponse;
};

/**
 * Response structure for an additional-asset content (download) URL.
 */
export interface AssetContentUrlResponse {
  success: boolean;
  data: {
    presignedUrl: string;
  };
  requestId?: string;
}

/**
 * Retrieves a presigned URL for a single additional asset addressed by its
 * project-relative `path` (e.g. `research/notes.md`). The path is validated and
 * existence-checked server-side before presigning.
 *
 * @param params - projectId and the project-relative asset path
 * @returns Promise resolving to the presigned URL response
 * @throws Error if the API request fails
 */
export const getAssetContentUrl = async (params: {
  projectId: string;
  path: string;
}): Promise<AssetContentUrlResponse> => {
  const restOperation = apiGet("/assets/contentUrl", {
    queryParams: { projectId: params.projectId, path: params.path },
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as AssetContentUrlResponse;
};

/**
 * API response wrapper for an asset upload URL request.
 */
export interface GetAssetUploadUrlResponse {
  success: boolean;
  data: AssetUploadUrlResponse;
  requestId?: string;
}

/**
 * Requests a presigned POST for uploading a single user file to the TACO
 * Assets bucket.
 *
 * Gated server-side behind the `fileUpload` feature flag (the endpoint returns
 * 404 for non-enrolled users). The returned `data.url` + `data.fields` are
 * passed to {@link uploadFileToS3} to perform the actual upload.
 *
 * @param request - projectId, filename, contentType and fileSize of the file
 * @returns Promise resolving to the presigned POST payload
 * @throws Error if the API request fails or returns invalid data
 */
export const getAssetUploadUrl = async (
  request: AssetUploadUrlRequest,
): Promise<GetAssetUploadUrlResponse> => {
  const restOperation = apiPost("/assets/uploadUrl", request);
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as GetAssetUploadUrlResponse;
};

/**
 * Uploads a single file's bytes directly to S3 using a presigned POST.
 *
 * Builds a multipart form from the signed `fields` (which must come first) plus
 * the file itself and POSTs it to the presigned `url`. This call goes straight
 * to S3 (not API Gateway), so it does not use the authenticated `apiPost`
 * helper. S3 independently enforces the size cap and Content-Type via the
 * signed policy and responds with a 2xx (typically 204) on success.
 *
 * @param presigned - the `url` + `fields` from {@link getAssetUploadUrl}
 * @param file - the browser File to upload
 * @throws Error if S3 responds with a non-2xx status
 */
export const uploadFileToS3 = async (
  presigned: Pick<AssetUploadUrlResponse, "url" | "fields">,
  file: File,
): Promise<void> => {
  const formData = new FormData();
  // The signed form fields must be appended before the file content.
  for (const [key, value] of Object.entries(presigned.fields)) {
    formData.append(key, value);
  }
  formData.append("file", file);

  const response = await fetch(presigned.url, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to upload file to S3: ${response.status} ${response.statusText}`,
    );
  }
};

/**
 * API response for the asset manifest endpoint.
 */
export interface GetAssetManifestResponse {
  success: boolean;
  data: {
    assets: AssetManifestEntry[];
  };
  requestId?: string;
}

/**
 * Fetches the manifest of all current assets for a project — every file that
 * exists in S3 with its last-modified timestamp.
 *
 * @param projectId - the project whose manifest to fetch
 * @returns Promise resolving to the manifest response
 * @throws Error if the API request fails
 */
export const getAssetManifest = async (
  projectId: string,
): Promise<GetAssetManifestResponse> => {
  const restOperation = apiGet("/assets/manifest", {
    queryParams: { projectId },
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as GetAssetManifestResponse;
};

/**
 * Fetches the on-demand HTML presentation for a project. The server generates
 * a self-contained HTML file from the latest project artifacts and returns it
 * directly (not stored in S3). Designed for the "Presentation (HTML)" download
 * option in the web app.
 *
 * @param projectId - the project to generate the presentation for
 * @returns Promise resolving to the raw HTML string
 * @throws Error if the API request fails
 */
export const getProjectPresentation = async (
  projectId: string,
): Promise<string> => {
  const restOperation = apiGet(
    `/projects/${encodeURIComponent(projectId)}/presentation`,
  );
  const { body } = await (await restOperation).response;
  return await body.text();
};

/**
 * API response for the uploaded-files listing endpoint.
 */
export interface ListUploadedFilesApiResponse {
  success: boolean;
  data: ListUploadedFilesResponse;
  requestId?: string;
}

/**
 * Fetches the files the user attached to a project, newest first.
 *
 * Uploads are excluded from both the additional-asset listing and the asset
 * manifest because they back no tab, so this is the only way to enumerate them.
 *
 * @param projectId - the project whose uploads to fetch
 * @returns Promise resolving to the uploaded-files response
 * @throws Error if the API request fails
 */
export const listUploadedFiles = async (
  projectId: string,
): Promise<ListUploadedFilesApiResponse> => {
  const restOperation = apiGet("/assets/uploads", {
    queryParams: { projectId },
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as ListUploadedFilesApiResponse;
};
